// Student ProfilePage — account details (read-only), personal details and
// payment preference (both editable, saved independently). Wrapped in the
// student sidebar layout (StudentLayout), unlike ProfileSetupPage.
import { useEffect, useRef, useState } from "react";
import type { FormEvent } from "react";
import { useAuth } from "../../hooks/useAuth.js";
import { useFetch, patchJson } from "../../hooks/useApi.js";
import { useProfile } from "../../contexts/ProfileContext.js";
import StatCard from "../../components/shared/StatCard.js";
import Avatar from "../../components/shared/Avatar.js";
import { IconLock, IconAlertTriangle } from "../../components/shared/icons.js";
import { KENYA_COUNTRY_CODE } from "@dm-lms/shared/constants";
import { studentProfileCompletion } from "@dm-lms/shared/utils";
import { studentPhoneSchema } from "@dm-lms/shared/validations";
import { phoneError } from "../../lib/phoneError.js";
import { PAYMENT_OPTIONS, PROFESSION_OPTIONS, ORGANISATION_HINT } from "../../lib/profileFormOptions.js";
import LocationFields from "../../components/student/LocationFields.js";
import type {
  PreferredPaymentMethod,
  Profession,
  Enrollment,
  Certificate,
  PaginatedResult,
} from "@dm-lms/shared/types";

const inputClass =
  "mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-dm-navy focus:border-dm-royal focus:outline-none focus:ring-1 focus:ring-dm-royal";

export default function ProfilePage() {
  const { user } = useAuth();
  const { profile, isLoading, error, refetch } = useProfile<"student">();
  const { data: enrollments } = useFetch<PaginatedResult<Enrollment>>("/api/enrollments/mine?page=1&pageSize=100");
  const { data: certificates } = useFetch<Certificate[]>("/api/certificates/mine");

  // ---- Card 2: personal details — seeded once from the first fetch, then
  // left alone (a later refetch, e.g. after Card 3 saves, must not clobber
  // whatever the user is mid-typing here). ----
  const seededPersonal = useRef(false);
  const [phone, setPhone] = useState("");
  const [phoneTouched, setPhoneTouched] = useState(false);
  const [idNumber, setIdNumber] = useState("");
  const [organization, setOrganization] = useState("");
  const [profession, setProfession] = useState<Profession | "">("");
  const [country, setCountry] = useState<string>(KENYA_COUNTRY_CODE);
  const [county, setCounty] = useState("");
  const [region, setRegion] = useState("");
  const [bio, setBio] = useState("");
  const [personalSaving, setPersonalSaving] = useState(false);
  const [personalSaved, setPersonalSaved] = useState(false);
  const [personalError, setPersonalError] = useState<string | null>(null);

  // ---- Card 3: payment preference — same seed-once approach. ----
  const seededPayment = useRef(false);
  const [paymentMethod, setPaymentMethod] = useState<PreferredPaymentMethod | null>(null);
  const [mpesaNumber, setMpesaNumber] = useState("");
  const [paymentSaving, setPaymentSaving] = useState(false);
  const [paymentSaved, setPaymentSaved] = useState(false);
  const [paymentError, setPaymentError] = useState<string | null>(null);

  useEffect(() => {
    if (!profile || seededPersonal.current) return;
    seededPersonal.current = true;
    setPhone(profile.phone ?? "");
    setIdNumber(profile.idNumber ?? "");
    setOrganization(profile.organization ?? "");
    setProfession(profile.profession ?? "");
    setCountry(profile.country ?? KENYA_COUNTRY_CODE);
    setCounty(profile.county ?? "");
    setRegion(profile.region ?? "");
    setBio(profile.bio ?? "");
  }, [profile]);

  useEffect(() => {
    if (!profile || seededPayment.current) return;
    seededPayment.current = true;
    setPaymentMethod(profile.preferredPaymentMethod);
    setMpesaNumber(profile.mpesaNumber ?? "");
  }, [profile]);

  const completion = profile
    ? studentProfileCompletion(profile)
    : { done: 0, total: 7, percent: 0 };

  async function savePersonal(event: FormEvent) {
    event.preventDefault();
    setPhoneTouched(true);
    if (phoneError(studentPhoneSchema, phone) !== null) return;
    setPersonalError(null);
    setPersonalSaving(true);

    // A blank phone is omitted: the server keeps the saved number (it cannot be cleared here).
    const res = await patchJson("/api/profile/student", {
      ...(phone.trim() !== "" ? { phone: phone.trim() } : {}),
      id_number: idNumber,
      ...(organization.trim() !== "" ? { organization } : {}),
      ...(profession !== "" ? { profession } : {}),
      country,
      ...(country === KENYA_COUNTRY_CODE ? { county } : { region }),
      bio,
    });

    setPersonalSaving(false);
    if (!res.ok) {
      setPersonalError(res.error);
      return;
    }
    setPersonalSaved(true);
    refetch();
    setTimeout(() => setPersonalSaved(false), 2000);
  }

  async function savePayment(event: FormEvent) {
    event.preventDefault();
    if (!paymentMethod) return;
    setPaymentError(null);
    setPaymentSaving(true);

    const res = await patchJson("/api/profile/student", {
      preferred_payment_method: paymentMethod,
      ...(paymentMethod === "mpesa" && mpesaNumber.trim() !== "" ? { mpesa_number: mpesaNumber } : {}),
    });

    setPaymentSaving(false);
    if (!res.ok) {
      setPaymentError(res.error);
      return;
    }
    setPaymentSaved(true);
    refetch();
    setTimeout(() => setPaymentSaved(false), 2000);
  }

  return (
    <div className="dm-page">
      <h1 className="text-2xl font-semibold text-dm-navy mb-6">Profile</h1>

      {isLoading && <p className="text-dm-slate text-sm">Loading profile…</p>}
      {error && <p className="text-red-600 text-sm">{error}</p>}

      {!isLoading && !error && profile && (
        <div className="max-w-2xl space-y-6">
          {/* CARD 1 — Account details (read-only) */}
          <div className="rounded-lg border border-gray-200 bg-white p-6">
            <div className="mb-5 flex items-center gap-4">
              <Avatar name={profile.name} size="md" />
              <div>
                <div className="flex items-center gap-1.5">
                  <p className="font-medium text-dm-navy">{profile.name}</p>
                  <IconLock width={14} height={14} className="text-dm-slate" />
                </div>
                <p className="text-xs text-dm-slate">Synced from Google</p>
              </div>
            </div>

            <div className="mb-6">
              <div className="flex items-center gap-1.5">
                <p className="text-sm text-dm-navy">{profile.email}</p>
                <IconLock width={14} height={14} className="text-dm-slate" />
              </div>
              <p className="text-xs text-dm-slate">Synced from Google</p>
            </div>

            <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
              <StatCard label="Student code" value={profile.code} />
              <StatCard
                label="Member since"
                value={
                  user ? new Date(user.createdAt).toLocaleDateString("en-US", { month: "short", year: "numeric" }) : "—"
                }
              />
              <StatCard label="Courses enrolled" value={enrollments?.total ?? 0} />
              <StatCard label="Certificates earned" value={(certificates ?? []).length} />
            </div>

            <div>
              <div className="mb-1.5 flex items-center justify-between">
                <span className="text-sm font-medium text-dm-navy">Profile completion</span>
                <span className="text-xs text-dm-slate">{completion.done} of {completion.total} required fields complete</span>
              </div>
              <div className="h-2 w-full rounded-full bg-gray-100">
                <div className="h-2 rounded-full bg-dm-royal transition-all" style={{ width: `${completion.percent}%` }} />
              </div>
            </div>

            {profile.idNumber === null && (
              <div className="mt-4 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2.5">
                <IconAlertTriangle width={16} height={16} className="mt-0.5 shrink-0 text-amber-600" />
                <p className="text-sm text-amber-800">Add your National ID to unlock certificate downloads.</p>
              </div>
            )}
          </div>

          {/* CARD 2 — Personal details (editable) */}
          <form onSubmit={savePersonal} className="rounded-lg border border-gray-200 bg-white p-6">
            <h2 className="mb-4 text-lg font-medium text-dm-navy">Personal details</h2>

            <div className="space-y-4">
              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-dm-navy">
                  Phone number
                </label>
                <input
                  id="phone"
                  type="tel"
                  value={phone}
                  onChange={(event) => setPhone(event.target.value)}
                  onBlur={() => setPhoneTouched(true)}
                  aria-invalid={phoneTouched && phoneError(studentPhoneSchema, phone) !== null}
                  aria-describedby={phoneTouched && phoneError(studentPhoneSchema, phone) !== null ? "phone-error" : undefined}
                  className={inputClass}
                />
                {phoneTouched && phoneError(studentPhoneSchema, phone) !== null && (
                  <p id="phone-error" role="alert" className="mt-1 text-xs text-red-600">
                    {phoneError(studentPhoneSchema, phone)}
                  </p>
                )}
              </div>

              <div>
                <label htmlFor="idNumber" className="block text-sm font-medium text-dm-navy">
                  National ID / Passport
                </label>
                <input
                  id="idNumber"
                  type="text"
                  value={idNumber}
                  onChange={(event) => setIdNumber(event.target.value)}
                  className={inputClass}
                />
              </div>

              <div>
                <label htmlFor="organization" className="block text-sm font-medium text-dm-navy">
                  Organization
                </label>
                <input
                  id="organization"
                  type="text"
                  value={organization}
                  onChange={(event) => setOrganization(event.target.value)}
                  className={inputClass}
                />
                <p className="mt-1 text-xs text-dm-slate">{ORGANISATION_HINT}</p>
              </div>

              <div>
                <label htmlFor="profession" className="block text-sm font-medium text-dm-navy">
                  Profession
                </label>
                <select
                  id="profession"
                  value={profession}
                  onChange={(event) => setProfession(event.target.value as Profession | "")}
                  className={inputClass}
                >
                  <option value="">Select…</option>
                  {PROFESSION_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <LocationFields
                country={country}
                county={county}
                region={region}
                onCountry={setCountry}
                onCounty={setCounty}
                onRegion={setRegion}
                inputClass={inputClass}
              />

              <div>
                <label htmlFor="bio" className="block text-sm font-medium text-dm-navy">
                  Bio
                </label>
                <textarea
                  id="bio"
                  rows={4}
                  maxLength={500}
                  value={bio}
                  onChange={(event) => setBio(event.target.value)}
                  className={inputClass}
                />
                <p className="mt-1 text-right text-xs text-dm-slate">{bio.length} / 500</p>
              </div>
            </div>

            {personalError && <p className="mt-3 text-sm text-red-600">{personalError}</p>}

            <div className="mt-4 flex items-center gap-3">
              <button
                type="submit"
                disabled={personalSaving}
                className="rounded-md bg-dm-navy px-4 py-2 text-sm font-medium text-white transition hover:bg-dm-blue disabled:cursor-not-allowed disabled:opacity-60"
              >
                {personalSaving ? "Saving…" : "Save changes"}
              </button>
              {personalSaved && <span className="text-sm text-green-600">Saved</span>}
            </div>
          </form>

          {/* CARD 3 — Payment preference (editable) */}
          <form onSubmit={savePayment} className="rounded-lg border border-gray-200 bg-white p-6">
            <h2 className="mb-1 text-lg font-medium text-dm-navy">Payment preference</h2>
            <p className="mb-4 text-sm text-dm-slate">How do you prefer to pay for courses?</p>

            <div className="grid grid-cols-2 gap-3">
              {PAYMENT_OPTIONS.map(({ value, label, Icon }) => (
                <button
                  key={value}
                  type="button"
                  aria-pressed={paymentMethod === value}
                  onClick={() => setPaymentMethod(value)}
                  className={`flex flex-col items-center gap-2 rounded-lg border-2 px-4 py-4 text-sm transition ${
                    paymentMethod === value
                      ? "border-dm-royal bg-dm-royal/5 text-dm-navy"
                      : "border-gray-200 text-dm-slate hover:border-gray-300"
                  }`}
                >
                  <Icon width={24} height={24} />
                  <span>{label}</span>
                </button>
              ))}
            </div>

            {paymentMethod === "mpesa" && (
              <div className="mt-4">
                <label htmlFor="mpesaNumber" className="block text-sm font-medium text-dm-navy">
                  M-Pesa number
                </label>
                <input
                  id="mpesaNumber"
                  type="tel"
                  placeholder="Leave blank to use your phone number above"
                  value={mpesaNumber}
                  onChange={(event) => setMpesaNumber(event.target.value)}
                  className={inputClass}
                />
              </div>
            )}

            {paymentError && <p className="mt-3 text-sm text-red-600">{paymentError}</p>}

            <div className="mt-4 flex items-center gap-3">
              <button
                type="submit"
                disabled={paymentSaving || !paymentMethod}
                className="rounded-md bg-dm-navy px-4 py-2 text-sm font-medium text-white transition hover:bg-dm-blue disabled:cursor-not-allowed disabled:opacity-60"
              >
                {paymentSaving ? "Saving…" : "Save changes"}
              </button>
              {paymentSaved && <span className="text-sm text-green-600">Saved</span>}
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
